import React, { useCallback, useRef } from 'react';

import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { IconButton, Typography } from '@cfa/react-components';

import { UnkeyedDocument } from '@/hooks/useGetStructuredBrowseData';

import Searchbar from '@/components/Searchbar/Searchbar';

import { ProcedureStep } from '../AddProcedurePopup';
import { PROCEDURE_STEPS } from '../constants';

import {
  BaseReferenceDto,
  DocumentDto,
  SearchDocument as Document,
} from '@/_generated/api-clients/xp';
import { store } from '@/atoms';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface AddProcedureHeaderProps {
  activeStep: ProcedureStep;
  searchTermDisplayed: string;
  setSearchTermDisplayed: (value: string) => void;
  previewDocument:
    | Document
    | BaseReferenceDto
    | UnkeyedDocument
    | Record<string, unknown>
    | DocumentDto;
  isPreviewResource: boolean;
  onSearchTermSelect: (suggestion: string | null) => void;
  handleBackButtonClick: (onClose?: () => void) => void;
  onClose: () => void;
  textVariant?: 'update' | 'default';
}

export const AddProcedureHeader: React.FC<AddProcedureHeaderProps> = ({
  activeStep,
  searchTermDisplayed,
  setSearchTermDisplayed,
  previewDocument,
  isPreviewResource,
  onSearchTermSelect,
  handleBackButtonClick,
  onClose,
  textVariant,
}) => {
  const { language } = store.get(userPrefsAtom);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const previewDocumentName =
    typeof previewDocument.name === 'string'
      ? (previewDocument.name as React.ReactNode)
      : typeof previewDocument.name === 'object'
        ? (previewDocument as DocumentDto).name?.[language]
        : undefined;

  const { t } = useTranslation();

  const onClearSearchClick = useCallback(() => {
    setSearchTermDisplayed('');
    searchInputRef.current?.focus();
  }, [setSearchTermDisplayed]);

  return (
    <>
      <div className="flex items-center justify-center">
        {activeStep > PROCEDURE_STEPS.CATEGORIES && (
          <IconButton
            data-testid="BackButton"
            onClick={() => handleBackButtonClick(onClose)}
            className="absolute! top-1! left-3!"
          >
            <IconArrowLeft />
          </IconButton>
        )}

        {isPreviewResource ? (
          <div>
            <Typography variant="overline2">
              {t('TrainingPlans.preview')}
            </Typography>
            {previewDocumentName}
          </div>
        ) : (
          <Typography variant="overline2">
            {textVariant === 'update'
              ? t('Generic.updateResource')
              : t('Generic.chooseResources')}
          </Typography>
        )}
      </div>
      {!isPreviewResource && (
        <Searchbar
          className="z-5 mt-6 mb-2"
          elevation={1}
          onChange={e =>
            setSearchTermDisplayed(e.target.value.length ? e.target.value : '')
          }
          onClear={onClearSearchClick}
          onSubmit={suggestion => {
            onSearchTermSelect(suggestion ?? null);
          }}
          placeholder={t('TrainingPlans.addResource.placeholder')}
          ref={searchInputRef}
          searchValue={searchTermDisplayed}
          showRecentSearchDropdown
          data-testid={
            textVariant === 'update'
              ? 'UpdateResourceSearchbar'
              : 'AddResourceSearchbar'
          }
        />
      )}
    </>
  );
};
